import { LiveAnnouncer } from '@angular/cdk/a11y';
import { BreakpointObserver } from '@angular/cdk/layout';
import { DatePipe } from '@angular/common';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { HistorialCambioPropiedadPropietarioDto } from '@models/historial-cambio-propiedad-propietario/historialCambioPropiedadPropietarioDto.model';
import { HistorialCambioPropiedadPropietarioService } from '@services/historial-cambio-propiedad-propietario/historial-cambio-propiedad-propietario.service';
import { SnackBarService } from '@services/snackBar/snack-bar-service.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { isFalsy } from 'utility-types';

@Component({
  selector: 'listado-historial-cambio-propietario',
  templateUrl: './listado-historial-cambio-propietario.component.html',
  styleUrls: ['./listado-historial-cambio-propietario.component.css'],
  providers: [DatePipe]
})
export class ListadoHistorialCambioPropietarioComponent implements OnInit {

  displayedColumns: string[] = [];
  dataSource = new MatTableDataSource<HistorialCambioPropiedadPropietarioDto>();
  data: boolean = false;
  formBuilder: FormBuilder = new FormBuilder();
  form: FormGroup;
  @Input() idPropiedad!:number

  constructor(
    private breakpointObserver: BreakpointObserver,
    private historialService: HistorialCambioPropiedadPropietarioService,
    private snackBar: SnackBarService,
    private spinner: NgxSpinnerService,
    private _liveAnnouncer: LiveAnnouncer,
    public dialog: MatDialog,
    private datePipe: DatePipe,
  ) {
    // Configuración responsiva de columnas
    this.breakpointObserver.observe(['(max-width: 768px)']).subscribe(result => {
      if (result.matches) {
        // Móvil: menos columnas
        this.displayedColumns = ['propiedad', 'propietarioNuevo', 'fechaCambio', 'accion'];
      } else {
        // Desktop: todas las columnas
        this.displayedColumns = [
          'propiedad',
          'propietarioAnterior',
          'propietarioNuevo',
          'fechaCambio',
          'motivo',
          'cantidadMovimientos',
        ];
      }
    });

    // Inicialización del formulario
    this.form = this.formBuilder.group({
      FechaDesde: [null],
      FechaHasta: [null],
      PropietarioBuscar: [''],
      PropiedadBuscar: [''],
    });
  }

  private paginator!: MatPaginator;
  private sort!: MatSort;

  @ViewChild(MatSort) set matSort(ms: MatSort) {
    this.sort = ms;
    this.setDataSourceAttributes();
  }

  @ViewChild(MatPaginator) set matPaginator(mp: MatPaginator) {
    this.paginator = mp;
    this.setDataSourceAttributes();
  }

  setDataSourceAttributes() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    // Configurar filtro personalizado
    this.dataSource.filterPredicate = (data: HistorialCambioPropiedadPropietarioDto, filter: string) => {
      const searchTerm = filter.toLowerCase();

      // Buscar en todos los campos relevantes
      const searchableText = [
        data.id?.toString(),
        data.propiedad?.ubicacion,
        data.propiedad?.localidad?.descripcion,
        data.propietarioAnterior?.persona?.descripcion,
        data.propietarioNuevo?.persona?.descripcion,
        data.motivo,
        data.fechaCambio,
        data.cantidadMovimientosAfectados?.toString()
      ].join(' ').toLowerCase();

      return searchableText.includes(searchTerm);
    };

    if (this.paginator && this.sort) {
      this.applyFilter(null);
    }
  }

  ngOnInit(): void {
    this.getAllData();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  getAllData() {
    this.spinner.show("spHistorial");
    this.historialService.getAllByIdPropiedad(this.idPropiedad)
      .subscribe({
        next: (data) => {
          if (data) {
            this.dataSource.data = data.sort((a, b) =>
              new Date(b.fechaCambio).getTime() - new Date(a.fechaCambio).getTime()
            );
            this.data = this.dataSource.data.length >= 1;
          }
          this.spinner.hide("spHistorial");
        },
        error: (error) => {
          this.spinner.hide("spHistorial");
          this.snackBar.showError(error, "Error al cargar datos");
        }
      });
  }

  getAllByFilter() {
    this.spinner.show("spHistorial");

    // Obtener valores del formulario
    const fechaDesde = this.form.controls["FechaDesde"].value;
    const fechaHasta = this.form.controls["FechaHasta"].value;
    const propietarioBuscar = this.form.controls["PropietarioBuscar"].value;
    const propiedadBuscar = this.form.controls["PropiedadBuscar"].value;

    // Por ahora usar getAll() y filtrar del lado cliente
    // TODO: Implementar filtros en el backend si es necesario
    this.historialService.getAllByIdPropiedad(this.idPropiedad)
      .subscribe({
        next: (data) => {
          let filteredData = data;

          // Filtrar por fechas
          if (fechaDesde) {
            filteredData = filteredData.filter(item =>
              new Date(item.fechaCambio) >= new Date(fechaDesde)
            );
          }
          if (fechaHasta) {
            filteredData = filteredData.filter(item =>
              new Date(item.fechaCambio) <= new Date(fechaHasta)
            );
          }

          // Filtrar por propietario
          if (propietarioBuscar && propietarioBuscar.trim()) {
            const propietarioFilter = propietarioBuscar.toLowerCase();
            filteredData = filteredData.filter(item =>
              item.propietarioAnterior?.persona?.descripcion?.toLowerCase().includes(propietarioFilter) ||
              item.propietarioNuevo?.persona?.descripcion?.toLowerCase().includes(propietarioFilter)
            );
          }

          // Filtrar por propiedad
          if (propiedadBuscar && propiedadBuscar.trim()) {
            const propiedadFilter = propiedadBuscar.toLowerCase();
            filteredData = filteredData.filter(item =>
              item.propiedad?.ubicacion?.toLowerCase().includes(propiedadFilter) ||
              item.propiedad?.localidad?.descripcion?.toLowerCase().includes(propiedadFilter)
            );
          }

          this.dataSource.data = filteredData.sort((a, b) =>
            new Date(b.fechaCambio).getTime() - new Date(a.fechaCambio).getTime()
          );
          this.data = this.dataSource.data.length >= 1;
          this.spinner.hide("spHistorial");
        },
        error: (error) => {
          this.spinner.hide("spHistorial");
          this.snackBar.showError(error, "Error al filtrar datos");
        }
      });
  }

  clearFilter() {
    this.form.reset();
    this.dataSource.filter = '';
    this.getAllData();
  }

  applyFilter(event: any) {
    if (event && event.target && event.target.value !== undefined) {
      let filterValue = (event.target as HTMLInputElement).value;
      this.dataSource.filter = filterValue.trim().toLowerCase();

      if (this.dataSource.paginator) {
        this.dataSource.paginator.firstPage();
      }
    }
  }

  openDialog(action: string, obj: HistorialCambioPropiedadPropietarioDto) {
    // TODO: Implementar diálogo de detalles
    console.log('Abrir diálogo:', action, obj);

    // Ejemplo de implementación:
    // const dialogRef = this.dialog.open(DialogViewHistorialComponent, {
    //   width: '80%',
    //   data: { action: action, item: obj },
    //   autoFocus: false
    // });
  }

  verMovimientosAfectados(element: HistorialCambioPropiedadPropietarioDto) {
    // TODO: Implementar vista de movimientos afectados
    console.log('Ver movimientos afectados:', element);
    this.snackBar.showInfo(`Movimientos afectados: ${element.cantidadMovimientosAfectados}`, "Información");
  }

  /** Announce the change in sort state for assistive technology. */
  announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Ordenado ${sortState.direction}ending`);
    } else {
      this._liveAnnouncer.announce('Ordenamiento limpiado');
    }
  }

  isFalsy(valor: any) {
    return isFalsy(valor);
  }

  trackByItems(index: number, item: any): any {
    return item.id || index;
  }
}
